import * as React from "react";

/**
 * Navbar visibility context for sharing navbar hidden/visible state
 * across components without requiring direct DOM queries.
 */

// Heights in pixels - exported for consumers that need to calculate positions
export const NAVBAR_HEIGHTS = {
  BANNER: 48,
  NAVBAR: 68,
  GAP: 24,
} as const;

export type NavbarVisibilityContextProps = {
  isHidden: boolean;
  setIsHidden: (hidden: boolean) => void;
};

const defaultNavbarVisibilityContext: NavbarVisibilityContextProps = {
  isHidden: false,
  setIsHidden: () => {},
};

export const NavbarVisibilityContext =
  React.createContext<NavbarVisibilityContextProps>(
    defaultNavbarVisibilityContext,
  );

export const NavbarVisibilityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isHidden, setIsHidden] = React.useState(false);

  const value = React.useMemo(() => ({ isHidden, setIsHidden }), [isHidden]);

  return (
    <NavbarVisibilityContext.Provider value={value}>
      {children}
    </NavbarVisibilityContext.Provider>
  );
};

export const useNavbarVisibility = (): NavbarVisibilityContextProps => {
  return React.useContext(NavbarVisibilityContext);
};
